//
// Copyright (c) 2025 Emplifi, Inc.
//

import {
  type DowncastConversionApi,
  type Editor,
  type EventInfo,
  type ModelElement,
  Plugin,
  toWidget,
  toWidgetEditable,
  type UpcastConversionApi,
  type UpcastConversionData,
  type ViewDocumentFragment,
  type ViewElement,
  Widget,
} from "ckeditor5";
import InsertCollapsibleSectionCommand from "./insertCollapsibleSectionCommand";
import {
  COLLAPSIBLE_SECTION,
  COLLAPSIBLE_SECTION_BODY,
  COLLAPSIBLE_SECTION_BODY_CSS_CLASS,
  COLLAPSIBLE_SECTION_CSS_CLASS,
  COLLAPSIBLE_SECTION_HEADER,
  COLLAPSIBLE_SECTION_HEADER_CSS_CLASS,
} from "./collapsibleSection.const";

export default class CollapsibleSectionEditing extends Plugin {
  // noinspection JSUnusedGlobalSymbols
  static get requires() {
    return [Widget];
  }

  // noinspection JSUnusedGlobalSymbols
  static get pluginName() {
    return "CollapsibleSectionEditing";
  }

  constructor(editor: Editor) {
    super(editor);
    this._setupConfig();
  }

  init() {
    this._setupSchema();
    this._setupConversions();
    this.editor.commands.add(
      "insertCollapsibleSection",
      new InsertCollapsibleSectionCommand(this.editor),
    );
  }

  private _setupConfig() {
    this.editor.config.define("collapsibleSection", {
      bodyName: "Section Body",
      bodyPlaceholder: "Section Body",
      headerName: "Section Header",
      headerPlaceholder: "Section Header",
      toolbarName: "Collapsible Section Toolbar",
      widgetName: "Collapsible Section",
    });
  }

  private _setupConversions() {
    const conversion = this.editor.conversion;
    conversion.for("upcast").add((dispatcher) => {
      dispatcher.on("element:details", this._upcastDetailsElement.bind(this));
    });
    conversion.for("dataDowncast").elementToElement({
      model: COLLAPSIBLE_SECTION,
      view: {
        classes: COLLAPSIBLE_SECTION_CSS_CLASS,
        name: "details",
      },
    });
    conversion.for("editingDowncast").elementToElement({
      model: COLLAPSIBLE_SECTION,
      view: (_modelItem: ModelElement, { writer }: DowncastConversionApi) => {
        const collapsibleSectionElement = writer.createContainerElement("div", {
          class: COLLAPSIBLE_SECTION_CSS_CLASS,
          open: true,
        });
        writer.setCustomProperty(COLLAPSIBLE_SECTION, true, collapsibleSectionElement);
        return toWidget(collapsibleSectionElement, writer, {
          label: this.editor.config.get("collapsibleSection.widgetName") as string,
        });
      },
    });
    conversion.for("dataDowncast").elementToElement({
      model: COLLAPSIBLE_SECTION_BODY,
      view: {
        classes: COLLAPSIBLE_SECTION_BODY_CSS_CLASS,
        name: "div",
      },
    });
    conversion.for("editingDowncast").elementToElement({
      model: COLLAPSIBLE_SECTION_BODY,
      view: (_modelItem: ModelElement, { writer }: DowncastConversionApi) => {
        const collapsibleSectionBodyElement = writer.createEditableElement("div", {
          class: COLLAPSIBLE_SECTION_BODY_CSS_CLASS,
        });
        writer.setCustomProperty(COLLAPSIBLE_SECTION_BODY, true, collapsibleSectionBodyElement);
        return toWidgetEditable(collapsibleSectionBodyElement, writer, {
          label: this.editor.config.get("collapsibleSection.bodyName") as string,
        });
      },
    });
    conversion.for("dataDowncast").elementToElement({
      model: COLLAPSIBLE_SECTION_HEADER,
      view: {
        classes: COLLAPSIBLE_SECTION_HEADER_CSS_CLASS,
        name: "summary",
      },
    });
    conversion.for("editingDowncast").elementToElement({
      model: COLLAPSIBLE_SECTION_HEADER,
      view: (_modelItem: ModelElement, { writer }: DowncastConversionApi) => {
        const headerElement = writer.createEditableElement("div", {
          class: COLLAPSIBLE_SECTION_HEADER_CSS_CLASS,
        });
        writer.setCustomProperty(COLLAPSIBLE_SECTION_HEADER, true, headerElement);
        return toWidgetEditable(headerElement, writer, {
          label: this.editor.config.get("collapsiblePanel.headerName") as string,
        });
      },
    });
  }

  private _setupSchema() {
    const schema = this.editor.model.schema;
    schema.register(COLLAPSIBLE_SECTION, {
      inheritAllFrom: "$blockObject",
    });
    schema.register(COLLAPSIBLE_SECTION_BODY, {
      isLimit: true,
      isSelectable: true,
      allowIn: COLLAPSIBLE_SECTION,
      allowContentOf: "$root",
    });
    schema.register(COLLAPSIBLE_SECTION_HEADER, {
      isLimit: true,
      isSelectable: true,
      allowIn: COLLAPSIBLE_SECTION,
      allowContentOf: "$block",
    });
  }

  private _upcastDetailsElement(
    _event: EventInfo,
    data: UpcastConversionData<ViewDocumentFragment>,
    conversionApi: UpcastConversionApi,
  ) {
    const { viewItem } = data;
    const collapsibleSectionConsumable = {
      name: true,
    };
    if (!conversionApi.consumable.test(viewItem, collapsibleSectionConsumable)) {
      return;
    }
    const collapsibleSectionModel = conversionApi.writer.createElement(COLLAPSIBLE_SECTION);
    if (!conversionApi.safeInsert(collapsibleSectionModel, data.modelCursor)) {
      return;
    }
    conversionApi.consumable.consume(viewItem, collapsibleSectionConsumable);
    const collapsibleSectionBodyConsumable = {
      classes: COLLAPSIBLE_SECTION_BODY_CSS_CLASS,
      name: true,
    };
    const collapsibleSectionHeaderConsumable = {
      name: true,
    };
    let collapsibleSectionBodyModel: ModelElement | undefined = undefined;

    const collapsibleSectionHeaderModel = conversionApi.writer.createElement(
      COLLAPSIBLE_SECTION_HEADER,
    );
    // Try to find an existing summary element, and convert it to a header
    for (let childIndex = 0; childIndex < viewItem.childCount; ++childIndex) {
      const childItem = viewItem.getChild(childIndex) as ViewElement;
      if (childItem.name === "summary") {
        conversionApi.consumable.consume(childItem, collapsibleSectionHeaderConsumable);
        conversionApi.convertChildren(childItem, collapsibleSectionHeaderModel);
      } else if (conversionApi.consumable.test(childItem, collapsibleSectionBodyConsumable)) {
        collapsibleSectionBodyModel = conversionApi.writer.createElement(COLLAPSIBLE_SECTION_BODY);
        conversionApi.consumable.consume(childItem, collapsibleSectionBodyConsumable);
        conversionApi.convertChildren(childItem, collapsibleSectionBodyModel);
      }
    }
    // Convert any remaining children of the details panel to children of the body
    if (!collapsibleSectionBodyModel) {
      collapsibleSectionBodyModel = conversionApi.writer.createElement(COLLAPSIBLE_SECTION_BODY);
      conversionApi.convertChildren(viewItem, collapsibleSectionBodyModel);
    }
    conversionApi.writer.append(collapsibleSectionHeaderModel, collapsibleSectionModel);
    conversionApi.writer.append(collapsibleSectionBodyModel, collapsibleSectionModel);
    conversionApi.updateConversionResult(collapsibleSectionModel, data);
  }
}
